import { Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  BookingUserRoleAssignmentsFilters,
  Group,
  GroupRegistrationRequestDto,
  GroupsAndMembersRoleAssignmentRequestDto,
  InvitationGroup,
  OrganizationUserRoleAssignmentsDto,
  Role,
  VariantRoleAssignmentLimitResponse,
  VariantRoleLimitFilter,
} from '@markant-platform/authorizer/model';
import { AppConfigService, buildParameters } from '@markant-platform/shared/data-access';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { UserIdDTO, UserNameDto, UserRole, UserRoles } from '@markant-platform/shared/user/model';
import { SearchAttributes, SortAttributes, TableParamConfig } from '@markant-platform/shared/table/model';
import { Page } from '@markant-platform/shared/data-model';

@Injectable({
  providedIn: 'root',
})
export class AuthorizerService {
  authorizerServiceApiUrl: string = AppConfigService.appConfiguration.apiUrl['authorizerService'];

  constructor(
    private readonly http: HttpClient,
    private readonly authenticatedUserService: AuthenticatedUserService
  ) {}

  updateAllowSkipMfaForRoles(
    allowSkipMfa: boolean,
    roles: { id: string; version: number }[],
    comment: string
  ): Observable<{
    updatedRoles: Role[];
    failures: { roleId: string; errorMessage: string }[];
  }> {
    if (roles.length === 0) return of({ updatedRoles: [], failures: [] });
    const dto = {
      allowSkipMfa: allowSkipMfa,
      comment: comment,
      roles: roles.map(role => {
        return { roleId: role.id, version: role.version };
      }),
    };
    return this.http.patch<{
      updatedRoles: Role[];
      failures: { roleId: string; errorMessage: string }[];
    }>(`${this.authorizerServiceApiUrl}/roles/allowSkipMfa`, dto);
  }

  getAllRolesAsPFAPaged(
    page: {
      index: number;
      size: number;
    },
    filter?: Record<string, string[]>,
    searchQuery?: string
  ): Observable<Page<Role>> {
    const queryParams: Record<string, string | string[]> = {};

    if (searchQuery) queryParams['query'] = searchQuery;
    if (filter) {
      for (const key of Object.keys(filter)) {
        queryParams[key] = filter[key];
      }
    }
    return this.getAuthorizerPaged<Role>('roles/all', queryParams, false, page);
  }

  getAssignableOrganizationRoles(organizationId?: string): Observable<Role[]> {
    const organizationIdParam = !organizationId ? '' : `&organization-id=${organizationId}`;
    return this.http.get<Role[]>(`${this.authorizerServiceApiUrl}/role?role-source=organization${organizationIdParam}`);
  }

  getAssignableDemoRoles(organizationId?: string): Observable<Role[]> {
    const organizationIdParam = !organizationId ? '' : `&organization-id=${organizationId}`;
    return this.http.get<Role[]>(`${this.authorizerServiceApiUrl}/role?role-source=demo${organizationIdParam}`);
  }

  getAssignableDynamicRoles(organizationId: string): Observable<Role[]> {
    const organizationIdParam = `&organization-id=${organizationId}`;
    return this.http.get<Role[]>(`${this.authorizerServiceApiUrl}/role?role-source=cartridges${organizationIdParam}`);
  }

  getPagedGroups(params?: TableParamConfig, searchCategory?: SearchAttributes): Observable<Page<Group>> {
    const index = params && params.page.index - 1;
    const size = params && params.page.size;
    const sort = params && params.sort;

    return this.authenticatedUserService
      .getOrganizationId()
      .pipe(switchMap(orgId => this.getPagedGroupsForOrganization(orgId, index, size, sort, searchCategory)));
  }

  getPagedGroupsForOrganization(
    organizationId: string,
    index?: number,
    size?: number,
    sort?: SortAttributes,
    search?: SearchAttributes
  ): Observable<Page<Group>> {
    const queryParams = buildParameters({
      index: index,
      size: size,
      sortField: sort?.sortField,
      sortOrder: sort?.sortOrder,
      query: search?.query,
      category: search?.category,
    });

    return this.http.get<Page<Group>>(`${this.authorizerServiceApiUrl}/organization/${organizationId}/group`, {
      params: queryParams,
    });
  }

  getInvitationGroups(organizationId: string): Observable<Page<InvitationGroup>> {
    const queryParams = buildParameters({
      index: 0,
      size: 100,
      sortField: 'displayName',
      sortOrder: 'asc',
      query: false,
      category: 'groups',
      includeVariantConstraints: true,
    });

    return this.http.get<Page<InvitationGroup>>(
      `${this.authorizerServiceApiUrl}/organization/${organizationId}/group`,
      {
        params: queryParams,
      }
    );
  }

  createGroup(createGroupDto: GroupRegistrationRequestDto): Observable<Group> {
    return this.authenticatedUserService
      .getOrganizationId()
      .pipe(
        switchMap(organizationId =>
          this.http.post<Group>(`${this.authorizerServiceApiUrl}/organization/${organizationId}/group`, createGroupDto)
        )
      );
  }

  changeGroupsUsers(usersToAdd: string[], usersToRemove: string[], groupId: string): Observable<void> {
    return this.authenticatedUserService.getOrganizationId().pipe(
      switchMap(organizationId =>
        this.http.patch<void>(`${this.authorizerServiceApiUrl}/organization/${organizationId}/group/${groupId}/users`, {
          usersToAdd,
          usersToRemove,
        })
      )
    );
  }

  getGroupsToRoleAssignments(roleId: string, index = 0, size = 100): Observable<Group[]> {
    return this.getAuthorizerPaged<Group>(
      'group/role-assignment',
      {
        'role-id': roleId,
        'include-suborgas': true,
      },
      true,
      { index: index, size: size }
    ).pipe(map(page => page.result));
  }

  getDirectUsersToRoleAssignments(
    roleId: string,
    organizationId: string,
    index = 0,
    size = 100
  ): Observable<UserIdDTO[]> {
    return this.getAuthorizerPaged<UserIdDTO>(
      'user/role-assignment',
      {
        'role-id': roleId,
        'organization-id': organizationId,
      },
      true,
      { index: index, size: size }
    ).pipe(map(result => result.result));
  }

  getGroupsAssignments(groupId: string, index = 0, size = 100): Observable<string[]> {
    return this.authenticatedUserService.getOrganizationId().pipe(
      switchMap(organizationId =>
        this.getAuthorizerPaged<{ userId: string }>(`organization/${organizationId}/group/${groupId}/user`, {}, true, {
          index: index,
          size: size,
        })
      ),
      map(page => page.result.map(userIdObj => userIdObj.userId))
    );
  }

  assignRoleToUser(params: {
    userId: string;
    organizationId: string;
    roleId: string;
    cartridgeId?: string;
    suborganizationId?: string;
  }): Observable<void> {
    const body: GroupsAndMembersRoleAssignmentRequestDto = {
      ...params,
      userIds: [params.userId],
      groupIds: [],
      suborganizationIds: params.suborganizationId ? [params.suborganizationId] : undefined,
    };
    return this.http.post<void>(`${this.authorizerServiceApiUrl}/role-assignment`, body);
  }

  assignRoleToGroup(params: {
    groupId: string;
    organizationId: string;
    roleId: string;
    cartridgeId: string;
    suborganizationId?: string;
  }): Observable<void> {
    const body: GroupsAndMembersRoleAssignmentRequestDto = {
      ...params,
      userIds: [],
      groupIds: [params.groupId],
      suborganizationIds: params.suborganizationId ? [params.suborganizationId] : undefined,
    };
    return this.http.post<void>(`${this.authorizerServiceApiUrl}/role-assignment`, body);
  }

  deleteGroup(groupId: string): Observable<void> {
    return this.authenticatedUserService
      .getOrganizationId()
      .pipe(
        switchMap(organizationId =>
          this.http.request<void>(
            'delete',
            `${this.authorizerServiceApiUrl}/organization/${organizationId}/group/${groupId}`
          )
        )
      );
  }

  removeUsersWithAssignNewServiceResponsibleAdmin(
    role: Role,
    organizationId: string,
    userIds: string[],
    newOwners?: {
      userId: string;
      cartridgeVariantId: string;
    }[]
  ): Observable<void> {
    const body: GroupsAndMembersRoleAssignmentRequestDto = {
      roleId: role.id,
      organizationId: organizationId,
      cartridgeId: role.cartridgeId || undefined,
      userIds: userIds,
      groupIds: [],
    };

    if (newOwners) {
      body.cartridgeVariantOwnerReplacements = newOwners;
    }

    return this.http.request<void>('delete', `${this.authorizerServiceApiUrl}/role-assignment`, { body: body });
  }

  removeUserFromRole(params: {
    userId: string;
    organizationId: string;
    roleId: string;
    catridgeId?: string;
    suborganizationId?: string;
  }): Observable<void> {
    return this.removeUsersAndGroupsFromRoleInOrganization({ ...params, userIds: [params.userId], groupIds: [] });
  }

  removeGroupFromRole(params: {
    groupId: string;
    organizationId: string;
    roleId: string;
    catridgeId?: string;
    suborganizationId?: string;
  }): Observable<void> {
    return this.removeUsersAndGroupsFromRoleInOrganization({ ...params, userIds: [], groupIds: [params.groupId] });
  }

  removeUsersAndGroupsFromRole(
    organizationId: string,
    role: Role,
    userAndGroups: { userIds: string[]; groupIds: string[] }
  ): Observable<void> {
    return this.removeUsersAndGroupsFromRoleInOrganization({
      userIds: userAndGroups.userIds,
      groupIds: userAndGroups.groupIds,
      roleId: role.id,
      cartridgeId: role.cartridgeId,
      organizationId: organizationId,
    });
  }

  private removeUsersAndGroupsFromRoleInOrganization(params: {
    userIds: string[];
    groupIds: string[];
    roleId: string;
    organizationId: string;
    cartridgeId?: string;
    suborganizationId?: string;
  }): Observable<void> {
    const body: GroupsAndMembersRoleAssignmentRequestDto = {
      ...params,
      suborganizationIds: params.suborganizationId ? [params.suborganizationId] : undefined,
    };
    return this.http.request<void>('delete', `${this.authorizerServiceApiUrl}/role-assignment`, { body: body });
  }

  getRoleAssignmentLimits(
    organizationId: string,
    queryParams?: VariantRoleLimitFilter
  ): Observable<VariantRoleAssignmentLimitResponse[]> {
    return this.http.get<VariantRoleAssignmentLimitResponse[]>(
      `${this.authorizerServiceApiUrl}/role-assignments/organization/${organizationId}/available`,
      { params: buildParameters(queryParams ?? {}) }
    );
  }

  getAllRolesForCartridges(): Observable<Role[]> {
    return this.http.get<Role[]>(`${this.authorizerServiceApiUrl}/role?role-source=cartridges`);
  }

  getAllRolesForCartridge(id: string, organizationId?: string): Observable<Role[]> {
    const queryParams = new HttpParams({
      fromObject: {
        'cartridge-id': id,
        ...(organizationId && {
          'organization-id': organizationId,
        }),
      },
    });

    return this.http.get<Role[]>(`${this.authorizerServiceApiUrl}/role`, { params: queryParams });
  }

  getRoleById(roleId: string): Observable<Role> {
    return this.http.get<Role>(`${this.authorizerServiceApiUrl}/role/${roleId}`);
  }

  getAllGroupsByUserId(userId: string, organizationId: string): Observable<Group[]> {
    const firstIndex = 0;
    const size = 100;
    const query = {
      'organization-id': organizationId,
    };

    return this.getAuthorizerPaged<Group>(`user/${userId}/group`, query, true, { index: firstIndex, size: size }).pipe(
      map(page => this.mapPageToResult<Group>(page))
    );
  }

  getPagedGroupMembers(
    groupId: string,
    index?: number,
    size?: number,
    sort?: SortAttributes
  ): Observable<Page<UserNameDto>> {
    const queryParams = buildParameters({
      index: index,
      size: size,
      sortField: sort?.sortField,
      sortOrder: sort?.sortOrder,
    });

    return this.authenticatedUserService
      .getOrganizationId()
      .pipe(
        switchMap(orgId =>
          this.http.get<Page<UserNameDto>>(
            `${this.authorizerServiceApiUrl}/organization/${orgId}/group/${groupId}/user?${queryParams}`
          )
        )
      );
  }

  getGroupById(groupId: string, params?: { includeRoles: boolean }): Observable<Group> {
    const queryParams = buildParameters(params as Record<string, boolean>);
    return this.authenticatedUserService.getOrganizationId().pipe(
      switchMap(orgId => {
        let url = `${this.authorizerServiceApiUrl}/organization/${orgId}/group/${groupId}`;
        if (params) {
          url += `?${queryParams}`;
        }
        return this.http.get<Group>(url);
      })
    );
  }

  updateGroupName(groupId: string, newName: string, version: number): Observable<Group> {
    return this.authenticatedUserService.getOrganizationId().pipe(
      switchMap(orgId =>
        this.http.patch<Group>(`${this.authorizerServiceApiUrl}/organization/${orgId}/group/${groupId}`, {
          displayName: newName,
          version,
        })
      )
    );
  }

  getCartridgeRolesForUser(userId: string, organizationId: string): Observable<UserRole[]> {
    const queryParams = buildParameters({
      'role-source': 'cartridges',
      'organization-id': organizationId,
    });
    return this.http
      .get<UserRoles>(`${this.authorizerServiceApiUrl}/user/${userId}/role-assignment?${queryParams}`)
      .pipe(map(({ roles }) => roles));
  }

  private mapPageToResult<T>(page: Page<T>): T[] {
    return page.result;
  }

  private getAuthorizerPaged<T>(
    path: string,
    queryParams: Record<string, string | number | boolean | string[]>,
    pageAll?: boolean,
    page?: {
      index: number;
      size: number;
    }
  ): Observable<Page<T>> {
    queryParams['index'] = page?.index ?? 0;
    queryParams['size'] = page?.size ?? 100;

    const params = buildParameters(queryParams);
    return this.http.get<Page<T>>(`${this.authorizerServiceApiUrl}/${path}`, { params: params }).pipe(
      switchMap(result => {
        if (pageAll && result.hasNext) {
          return this.getAuthorizerPaged<T>(path, queryParams, true, {
            index: result.pageIndex + 1,
            size: result.pageSize,
          }).pipe(
            map(pagedResult => {
              return {
                result: result.result.concat(pagedResult.result),
                hasNext: pagedResult.hasNext,
                pageIndex: result.pageIndex,
                pageSize: result.pageSize + pagedResult.pageSize,
                totalCount: result.totalCount,
              };
            })
          );
        }
        return of(result);
      })
    );
  }

  getUserRoleAssignmentsForBooking(
    id: string,
    queryParams: BookingUserRoleAssignmentsFilters = {}
  ): Observable<OrganizationUserRoleAssignmentsDto> {
    const params = buildParameters(queryParams);
    return this.http.get<OrganizationUserRoleAssignmentsDto>(
      `${this.authorizerServiceApiUrl}/bookings/${id}/user-role-assignment`,
      { params }
    );
  }
}
